// @ts-nocheck

import * as Sentry from '@sentry/react';
import React, { useState, useCallback, useEffect } from 'react';

import Client from '../../client';
import { ModalFormat } from '../../constants/ui/modal';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { Modal as ModalTheme } from '../../styled-components/dark/modal';
import {
  UserNotificationButton,
  UserNotificationCloseButton,
  UserNotificationContent,
  UserNotificationCtaWrapper,
  UserNotificationFetchedContent,
} from '../../styled-components/shared';
import type { AsyncContentProps } from '../../types/userNotification';
import { getCustomHeaders } from '../../utils';
import fetch from '../../utils/fetch';
import Modal from '../Modal';
import ModalFooter from '../ModalFooter';

const UserActionsCtas = ({ acceptUrl, jwt, acceptLabel, backgroundColor, fallbackAcceptFn, rejectFn, rejectLabel }) => {
  const accept = useCallback(async () => {
    try {
      const payload = await fetch(acceptUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body: `params=${JSON.stringify({ command: 'validate' })}`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...getCustomHeaders({ jwt, tracker: Client.tracker }),
        },
      });
      const json = await payload.json();
      if (json.ECODE === 0 || json.ECODE === 3) {
        fallbackAcceptFn();
      }
    } catch (err) {
      console.error(err);
    }
  }, [acceptUrl, fallbackAcceptFn, jwt]);

  return (
    <UserNotificationCtaWrapper $backgroundColor={backgroundColor}>
      <UserNotificationButton onClick={accept}>{acceptLabel}</UserNotificationButton>
      {rejectFn ? <UserNotificationCloseButton onClick={rejectFn}>{rejectLabel}</UserNotificationCloseButton> : null}
    </UserNotificationCtaWrapper>
  );
};

/**
 * Display the user data content like the CGU or account information
 */
const UserNotificationValidationContent = ({
  url,
  title,
  jwt,
  acceptLabel,
  accept,
  reject,
  rejectLabel,
}: AsyncContentProps) => {
  const [content, setContent] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const payload = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        body: `params=${JSON.stringify({ command: 'get' })}`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...getCustomHeaders({ jwt, tracker: Client.tracker }),
        },
      });
      const json = await payload.json();
      if (json.ECODE === 0) {
        setContent(json.ADDITIONAL_MESSAGE);
      } else if (json.ECODE === 3) {
        Sentry.captureMessage(`UserValidationContent - Error code 3`, { extra: { json } });
        accept();
      }
    } catch (err) {
      console.error(err);
    }
  }, [accept, jwt, url]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return (
    <Modal
      backgroundColor={ModalTheme.backgroundColor.default}
      format={ModalFormat.FULLSCREEN}
      zIndex={theme.ZIndices.navBar}
    >
      <>
        <UserNotificationContent>
          {title ? <h2>{title}</h2> : null}
          {content ? (
            <UserNotificationFetchedContent dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <UserNotificationFetchedContent>{__('Loading in progress')}</UserNotificationFetchedContent>
          )}
        </UserNotificationContent>
        <ModalFooter
          render={() => (
            <UserActionsCtas
              jwt={jwt}
              acceptUrl={url}
              acceptLabel={acceptLabel}
              backgroundColor={theme.Cgu.backgroundColor}
              fallbackAcceptFn={accept}
              rejectFn={reject}
              rejectLabel={rejectLabel}
            />
          )}
        />
      </>
    </Modal>
  );
};

export default UserNotificationValidationContent;
